import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import {
  getGroupDescriptionElement,
  getGroupIconElement,
  getGroupTextElement,
} from "@/utils/groupElementType";
import { getMessage } from "@/utils/message";
import {
  Add,
  CloudApp,
  InformationFilled,
  ServiceId,
  Warning,
} from "@carbon/icons-react";
import { AxiosError } from "axios";
import { Field, Form, Formik } from "formik";
import React, { FC, ReactNode, memo, useRef, useState } from "react";
import "react-multi-carousel/lib/styles.css";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { createGroup } from "../../api/group.api";
import { GroupModeEnums, groupModeList } from "../../constants/group.constant";
import { GroupPathsEnum } from "../../constants/group.paths";
import { GroupInfoDef } from "../../types/group.type";
import AddMembersModal from "./AddMembersModal";
import OnboardingModal from "./OnboardingModal";

export type CreateGroupModalProps = {
  rootGroup?: Partial<GroupInfoDef>;
  isFullWidth?: boolean;
  button?: ReactNode;
  rootMode?: GroupModeEnums;
  rootId?: number;
  haveSubGroup?: boolean;
};

export type Initial = {
  description: string;
  name: string;
  mode: GroupModeEnums;
  isTagaCloud: boolean;
  parentId?: number;
  inviteOwner?: boolean;
  membersSubGroup?: number[];
  ownerSubGroup?: number;
};

export const locationOptions = [
  {
    label: "TAGA Cloud",
    value: true,
    icon: (
      <CloudApp
        size={16}
        className="text-gray-500 mt-0.5 shrink-0"
      />
    ),
    description: "Dữ liệu của tổ chức sẽ được lưu trên TAGA cloud",
  },
  {
    label: "Thiết bị tổ chức",
    value: false,
    icon: (
      <ServiceId
        size={16}
        className="text-gray-500 mt-0.5 shrink-0"
      />
    ),
    description: "Dữ liệu của tổ chức sẽ được lưu trên Thiết bị tổ chức",
  },
];

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
  },
};

const CreateGroupModal: FC<CreateGroupModalProps> = ({
  isFullWidth,
  button,
  rootMode = GroupModeEnums.PUBLIC,
  rootId,
  rootGroup,
  haveSubGroup,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const CreateGroupSchema = Yup.object().shape({
    name: Yup.string()
      .max(
        50,
        `Tên ${rootId ? "nhóm" : "tổ chức"} không được vượt quá 50 ký tự`
      )
      .trim()
      .required(`Tên ${rootId ? "nhóm" : "tổ chức"} không được bỏ trống.`),
    description: Yup.string().max(
      400,
      `Mô tả ${rootId ? "nhóm" : "tổ chức"} không vượt quá 400 ký tự`
    ),
  });

  const initialValues: Initial = {
    name: "",
    description: "",
    mode: rootMode,
    isTagaCloud: true,
  };

  const pendingSubmitData = useRef<Initial | null>(null);

  const [existingNameError, setExistingNameError] = useState<null | string>(
    null
  );
  const [openModal, setOpenModal] = useState(false);
  const [openOnboarding, setOpenOnboarding] = useState(false);
  const [openAddMembers, setOpenAddMembers] = useState(false);

  const setPendingSubmitData = (values: Initial | null) => {
    pendingSubmitData.current = values;
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setExistingNameError(null);
    setPendingSubmitData(null);
  };

  const createGroupFn = async ({
    values,
    setSubmitting,
  }: {
    values: Initial;
    setSubmitting: (value: boolean) => void;
  }) => {
    try {
      if (rootId) {
        values.parentId = rootId;
      }
      values.mode = Number(values.mode);
      const newGroup = await createGroup(values);
      navigate(
        GroupPathsEnum.GROUP_DETAIL.replace(":id", String(newGroup.data.id))
      );
      toast({
        title: "Thành công!",
        variant: "success",
        description: getMessage("MSG3", `${rootId ? "nhóm" : "tổ chức"}`),
      });
      setOpenModal(false);
    } catch (error: any) {
      if (error instanceof AxiosError) {
        if (error.response?.data.errors.includes("MSG2")) {
          setExistingNameError(
            "Tên nhóm đã tồn tại, vui lòng điền giá trị khác."
          );
          toast({
            title: "Thất bại!",
            variant: "error",
            description: getMessage("MSG2", values.name),
          });
        }
      } else {
        setExistingNameError(null);
        toast({
          title: "Thất bại!",
          variant: "error",
          description: getMessage(error.response?.data.errors[0]),
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={openModal}
        onOpenChange={(open) => {
          if (open) {
            setExistingNameError(null);
          } else {
            handleCloseModal();
          }
        }}
      >
        <DialogTrigger asChild>
          <div
            onClick={() =>
              haveSubGroup ? setOpenModal(true) : setOpenOnboarding(true)
            }
          >
            {button ? (
              button
            ) : (
              <Button
                onClick={(e) => e.preventDefault()}
                iconBefore={<Add size={20} />}
                className={`${
                  isFullWidth ? "w-full" : ""
                } h-11 px-4 py-2.5 text-sm font-medium transition-colors `}
              >
                Tạo tổ chức
              </Button>
            )}
          </div>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[580px] bg-white p-0 gap-0 rounded-lg border border-gray-200 shadow-lg">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {rootId ? "Tạo nhóm mới" : "Tạo tổ chức mới"}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600 mt-1">
              {rootId ? (
                <span className="gap-1">
                  Nhóm mới sẽ trực thuộc tổ chức này. Bạn có thể tuỳ chỉnh vị
                  trí trong
                  <span className="font-medium ml-1">Cơ cấu tổ chức</span>
                </span>
              ) : (
                "Thiết lập tổ chức mới để bắt đầu quản lý nhóm và dự án của bạn"
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-6">
            <Formik
              initialValues={initialValues}
              validationSchema={CreateGroupSchema}
              onSubmit={(values: Initial, { setSubmitting }) => {
                if (rootId) {
                  setPendingSubmitData(values);
                  setSubmitting(false);
                  setOpenAddMembers(true);
                } else {
                  createGroupFn({ values, setSubmitting });
                }
              }}
            >
              {({
                handleChange,
                setFieldValue,
                errors,
                isSubmitting,
                isValid,
              }) => (
                <Form className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        Tên {rootId ? "nhóm" : "tổ chức"}{" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <Field
                        name="name"
                        as={Input}
                        placeholder={`Nhập tên ${
                          rootId ? "nhóm" : "tổ chức"
                        }...`}
                        className="h-10 border-gray-200 focus:border-gray-400 focus:ring-0"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (e.currentTarget.value === "") {
                            setExistingNameError(null);
                          }
                          handleChange(e);
                        }}
                        error={errors.name || existingNameError}
                      />
                      {(errors.name || existingNameError) && (
                        <p className="text-red-500 text-xs flex items-center gap-1">
                          <Warning size={14} />
                          {errors.name || existingNameError}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                        Quyền truy cập
                        {rootId && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InformationFilled
                                  size={14}
                                  className="text-gray-400"
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  Quyền truy cập của nhóm phụ thuộc vào quyền
                                  truy cập của tổ chức
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </Label>
                      <Select
                        defaultValue={String(rootMode)}
                        onValueChange={(value) => setFieldValue("mode", value)}
                        disabled={!!rootId}
                      >
                        <SelectTrigger className="border-gray-200 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup className="max-w-[520px]">
                            {groupModeList.map((mode) => (
                              <SelectItem
                                key={mode}
                                value={String(mode)}
                              >
                                <div className="flex items-start gap-3 py-2">
                                  {getGroupIconElement({
                                    mode,
                                    size: 16,
                                    className: "text-gray-500 mt-0.5 shrink-0",
                                  })}
                                  <div className="text-left">
                                    <p className="text-sm font-medium text-gray-900">
                                      {getGroupTextElement({ mode })}
                                    </p>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {getGroupDescriptionElement({
                                        mode,
                                        isOrganization: !rootId,
                                      })}
                                    </p>
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                        Nơi lưu
                        {rootId && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InformationFilled
                                  size={14}
                                  className="text-gray-400"
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  Nơi lưu trữ của nhóm phụ thuộc vào nơi lưu trữ
                                  của tổ chức
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </Label>
                      <Select
                        defaultValue={(() => {
                          if (rootGroup?.savePlaceOption) {
                            return rootGroup?.savePlaceOption === "taga-cloud"
                              ? "true"
                              : "false";
                          }
                          return "true";
                        })()}
                        onValueChange={(value) =>
                          setFieldValue(
                            "isTagaCloud",
                            Boolean(value === "true")
                          )
                        }
                        disabled={!!rootId}
                      >
                        <SelectTrigger className="border-gray-200 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup className="max-w-[520px]">
                            {locationOptions.map((option, index) => (
                              <SelectItem
                                key={index}
                                value={String(option.value)}
                              >
                                <div className="flex items-start gap-3 py-2">
                                  {option.icon}
                                  <div className="text-left">
                                    <p className="text-sm font-medium text-gray-900">
                                      {option.label}
                                    </p>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {option.description}
                                    </p>
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        Mô tả {rootId ? "nhóm" : "tổ chức"}
                      </Label>
                      <Field
                        name="description"
                        as={Textarea}
                        placeholder={`Mô tả ngắn gọn về ${
                          rootId ? "nhóm" : "tổ chức"
                        } của bạn...`}
                        className="min-h-[80px] border-gray-200 focus:border-gray-400 focus:ring-0 resize-none"
                        onChange={handleChange}
                        error={errors.description}
                      />
                      {errors.description && (
                        <p className="text-red-500 text-xs flex items-center gap-1">
                          <Warning size={14} />
                          {errors.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <DialogFooter className="pt-4 border-t border-gray-100 gap-2">
                    <Button
                      variant="outline"
                      type="button"
                      onClick={handleCloseModal}
                      disabled={isSubmitting}
                      className="h-10 px-4 border-gray-200 text-gray-700 hover:bg-gray-50"
                    >
                      Hủy bỏ
                    </Button>
                    <Button
                      type="submit"
                      disabled={!isValid || isSubmitting}
                      loading={isSubmitting}
                      className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {rootId ? "Thêm thành viên" : "Tạo tổ chức"}
                    </Button>
                  </DialogFooter>
                </Form>
              )}
            </Formik>
          </div>
        </DialogContent>
      </Dialog>

      <OnboardingModal
        responsive={responsive}
        openOnboarding={openOnboarding}
        setOpenOnboarding={setOpenOnboarding}
        setOpenModal={setOpenModal}
      />
      <AddMembersModal
        openModal={openModal}
        rootGroup={rootGroup}
        initialValues={initialValues}
        pendingSubmitData={pendingSubmitData.current}
        openAddMembers={openAddMembers}
        setOpenAddMembers={setOpenAddMembers}
        createGroupFn={createGroupFn}
      />
    </>
  );
};

export default memo(CreateGroupModal);
